import React, { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { stringFormat, T } from 'src/shared/helpers';
import PatternIcon from 'src/shared/material_icons/device/pattern.svg';
import SearchIcon from 'src/shared/material_icons/action/search.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import { CamerasIcon, MaterialsIcon, ObjectsIcon } from 'src/components/widgets/icons/scene';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { panel, PropertiesPanelLookProvider, PropertiesPanelResourceProperty, PropertiesPanelSegmented } from '../scene-properties-panel';

// I CONTROLLI DELLO SCENE EDITOR NELL'INSPECTOR DEL COMPOSER (utente, 2026-10-15; prototipo V2). Una
// scelta fra due o tre voci è la scelta a segmenti, larga quanto la card; un posto che tiene una cosa —
// la camera, gli oggetti, l'attributo di un Link, l'opzione dello store — è lo slot: il glifo in un
// riquadro, il nome, che cos'è, e la riga si apre sull'elenco. Prima erano radio in fila e tendine di
// react-select: un'altra grammatica a un pannello di distanza.

export type ComposerSlotOption = {
    value: string;
    label: string;
    caption?: string;
    /** La faccia della voce, quando ne ha una sua: l'immagine di un'opzione. Senza, il glifo dello slot. */
    preview?: ReactNode;
};

// IL GLIFO STA IN UN RIQUADRO, come nella marca della testata: è il segno della specie, non una foto.
export const ComposerSlotGlyph = styled.span`
    display: inline-flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border-radius: 6px;
    background: ${panel.ground};
    box-shadow: inset 0 0 0 1px ${panel.line};
    color: ${panel.inkMuted};

    svg {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

// UN ERRORE SI VEDE SUL POSTO: il bordo dello slot — o di quello vuoto, in un elenco — si fa rosso.
const SlotFrame = styled.div<{ $error?: boolean }>`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 6px;

    ${(props) =>
        props.$error &&
        `&[data-slot-error] [id],
        [data-slot-error] [id] {
            border-color: ${props.theme.app.component.button.destructive.default.backgroundColor};
        }`}
`;

/** Una scelta fra poche voci, a tutta riga. I valori arrivano come sono — numeri compresi — e tornano uguali. */
export function ComposerChoice<V extends string | number>({
    options,
    value,
    onChange,
    ariaLabel,
    disabled
}: {
    options: { value: V; label: string }[];
    value: V;
    onChange: (value: V) => void;
    ariaLabel: string;
    disabled?: boolean;
}) {
    return (
        <div data-composer-wide>
            <PropertiesPanelSegmented
                fill
                disabled={disabled}
                ariaLabel={ariaLabel}
                options={options.map((option) => ({ value: String(option.value), label: option.label }))}
                value={String(value)}
                onChange={(chosen) => {
                    const picked = options.find((option) => String(option.value) === chosen);
                    if (picked) onChange(picked.value);
                }}
            />
        </div>
    );
}

type SlotCommon = {
    id: string;
    options: ComposerSlotOption[];
    /** Il segno della specie, per le voci senza una faccia loro. */
    glyph: ReactNode;
    /** Sotto il nome, quando la voce non dice di più: «Camera», «Object». */
    caption?: string;
    emptyLabel: string;
    searchPlaceholder?: string;
    /** Che cosa dice l'elenco quando non c'è niente da scegliere. */
    noOptionsLabel?: string;
    isError?: boolean;
    disabled?: boolean;
};

const glyphed = (content: ReactNode) => <ComposerSlotGlyph>{content}</ComposerSlotGlyph>;
const pickerOptions = (options: ComposerSlotOption[], glyph: ReactNode) =>
    options.map((option) => ({ value: option.value, label: option.label, caption: option.caption, mark: glyphed(option.preview ?? glyph) }));

/** UN POSTO, UNA COSA: si sceglie dall'elenco e, se si può fare a meno, si svuota col cestino. */
export const ComposerSlot: FC<
    SlotCommon & {
        value?: string;
        onChange: (value: string) => void;
        /** Si può lasciare vuoto: c'è il cestino. */
        clearable?: boolean;
        /** Il nome di una voce che l'elenco non ha più. */
        missingLabel?: string;
        /** IL CLIC SULLO SLOT RITROVA LA VOCE SCELTA (prototipo), come gli slot della scena; si cambia dal menu. */
        onReveal?: (value: string) => void;
    }
> = ({ id, value, options, glyph, caption, emptyLabel, searchPlaceholder, noOptionsLabel, isError, disabled, onChange, clearable, missingLabel, onReveal }) => {
    const current = value ? (options.find((option) => option.value === value) ?? { value, label: missingLabel ?? value }) : undefined;
    return (
        // LO SLOT HA SEMPRE IL VESTITO DELLA SCENA: fuori da una sezione — una card d'azione, un'espressione dei
        // Links — prendeva quello vecchio, con il filo tratteggiato sotto e il proprio respiro.
        <PropertiesPanelLookProvider value='scene'>
            <SlotFrame $error={isError} data-slot-error data-composer-wide>
                <PropertiesPanelResourceProperty
                    id={id}
                    preview={current ? glyphed(current.preview ?? glyph) : undefined}
                    name={current?.label}
                    caption={current?.caption ?? caption}
                    emptyLabel={emptyLabel}
                    options={pickerOptions(options, glyph)}
                    value={current?.value}
                    searchPlaceholder={searchPlaceholder}
                    noOptionsLabel={noOptionsLabel}
                    disabled={disabled}
                    onSelect={(chosen) => onChange(String(chosen))}
                    onClear={current && clearable ? () => onChange('') : undefined}
                    onReveal={current && onReveal ? () => onReveal(current.value) : undefined}
                />
            </SlotFrame>
        </PropertiesPanelLookProvider>
    );
};

/**
 * TANTE COSE: UNO SLOT PER COSA, E UNO VUOTO PER AGGIUNGERNE (prototipo, «Choose an object»). Ognuno
 * si sostituisce o si toglie da sé; l'elenco non ripropone quelle già prese. Con un errore, si accende
 * il posto vuoto: è lì che manca qualcosa.
 */
export const ComposerSlotList: FC<
    SlotCommon & {
        value: string[];
        onChange: (values: string[]) => void;
        missingLabel?: string;
        /** Una voce da aggiungere che nasce dal testo cercato: un modello di nome. Restituisce il valore da scrivere. */
        fromQuery?: (query: string) => (Omit<ComposerSlotOption, 'preview'> & { mark?: ReactNode }) | null;
        /** Il clic su uno slot ritrova la sua voce. */
        onReveal?: (value: string) => void;
        /** Righe in più prima dello slot vuoto: i modelli di nome. */
        extraRows?: ReactNode;
        /** Il comando in fondo all'elenco dello slot vuoto («Match by name…»). */
        footer?: { label: string; caption?: string; mark?: ReactNode; onClick: () => void };
    }
> = ({
    id,
    value,
    options,
    glyph,
    caption,
    emptyLabel,
    searchPlaceholder,
    noOptionsLabel,
    isError,
    disabled,
    onChange,
    missingLabel,
    fromQuery,
    onReveal,
    extraRows,
    footer
}) => {
    const optionOf = (chosen: string) => options.find((option) => option.value === chosen) ?? { value: chosen, label: missingLabel ?? chosen };
    const free = (keep?: string) => options.filter((option) => option.value === keep || !value.includes(option.value));
    return (
        <PropertiesPanelLookProvider value='scene'>
            <SlotFrame $error={isError} data-composer-wide>
                {value.map((chosen, index) => {
                    const current = optionOf(chosen);
                    return (
                        <PropertiesPanelResourceProperty
                            key={chosen}
                            id={`${id}-${index}`}
                            preview={glyphed(current.preview ?? glyph)}
                            name={current.label}
                            caption={current.caption ?? caption}
                            options={pickerOptions(free(chosen), glyph)}
                            value={chosen}
                            searchPlaceholder={searchPlaceholder}
                            disabled={disabled}
                            onSelect={(picked) => onChange(value.map((candidate) => (candidate === chosen ? String(picked) : candidate)))}
                            onClear={() => onChange(value.filter((candidate) => candidate !== chosen))}
                            onReveal={onReveal ? () => onReveal(chosen) : undefined}
                        />
                    );
                })}
                {extraRows}
                {(free().length > 0 || (options.length === 0 && noOptionsLabel) || fromQuery || footer) && (
                    <div data-slot-error>
                        <PropertiesPanelResourceProperty
                            id={`${id}-new`}
                            emptyLabel={emptyLabel}
                            onFooter={footer}
                            options={pickerOptions(free(), glyph)}
                            searchPlaceholder={searchPlaceholder}
                            noOptionsLabel={noOptionsLabel}
                            fromQuery={
                                fromQuery &&
                                ((query) => {
                                    const entry = fromQuery(query);
                                    return (
                                        entry && {
                                            label: entry.label,
                                            caption: entry.caption,
                                            mark: entry.mark,
                                            onClick: () => onChange([...value, entry.value])
                                        }
                                    );
                                })
                            }
                            disabled={disabled}
                            onSelect={(picked) => onChange([...value, String(picked)])}
                        />
                    </div>
                )}
            </SlotFrame>
        </PropertiesPanelLookProvider>
    );
};

// IL GUID TUTTO ZERI È «NESSUNA CAMERA»: così la salva il Backoffice, e lo slot lo mostrava come un nome.
const NO_CAMERA = '00000000-0000-0000-0000-000000000000';

/** La posizione di camera. */
export const ComposerCameraSlot: FC<{
    id: string;
    value?: string;
    options: ComposerSlotOption[];
    onChange: (value: string) => void;
    isError?: boolean;
    /** Si può lasciare vuota: c'è il cestino. Un'azione «Move to Camera» invece una camera la vuole. */
    clearable?: boolean;
}> = ({ clearable = true, ...props }) => (
    <ComposerSlot
        {...props}
        value={props.value && props.value !== NO_CAMERA ? props.value : undefined}
        noOptionsLabel={T._('The scene has no saved cameras yet.', 'Admin')}
        glyph={<CamerasIcon />}
        caption={T._('Camera', 'Admin')}
        emptyLabel={T._('Choose a camera position', 'Admin')}
        searchPlaceholder={T._('Search cameras…', 'Admin')}
        clearable={clearable}
    />
);

// I MODELLI DI NOME (utente, 2026-10-15). Nei bersagli di un'azione il motore legge ogni voce come un modello:
// «*» vale qualunque testo, e il confronto col nome della mesh ignora maiuscole e minuscole — «*» prende tutte
// le mesh, «Seat*» quelle che portano «Seat» nel nome. Il Backoffice li accettava già: qui si scrivono nella
// ricerca dello slot e diventano uno slot loro, che dice quante mesh prendono adesso.
const patternOf = (text: string) => new RegExp(text.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*'), 'i');
const isPattern = (text: string) => text.includes('*');

type PatternTexts = {
    glyph: ReactNode;
    caption: string;
    emptyLabel: string;
    noOptionsLabel: string;
    searchLabel: string;
    allLabel: string;
    countLabel: (count: number) => string;
};

// LA RIGA DI UN MODELLO DI NOME (prototipo, «Match by name»; utente 2026-10-21): lente, il testo che si scrive, quanti
// ne prende adesso, la X. Si scrive al posto, senza una finestra; Invio o uscire dal campo lo fissano, Esc lo annulla.
const PatternRowFrame = styled.label`
    display: flex;
    min-height: 34px;
    align-items: center;
    gap: 8px;
    padding: 0 4px 0 6px;
    border: 1px solid var(--editor-line, #d1d3d8);
    border-radius: 8px;
    background: var(--editor-field, #f1f2f4);
    color: var(--editor-fg, #262a30);
    font-size: 12px;

    &:focus-within {
        border-color: var(--editor-accent, #a94700);
    }

    > svg {
        flex: none;
        width: 16px;
        height: 16px;
        fill: var(--editor-muted, #55595f);
    }

    input {
        flex: 1;
        min-width: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        outline: none;
    }

    > span {
        flex: none;
        color: var(--editor-faint, #676a6e);
        font-size: 11px;
        font-variant-numeric: tabular-nums;
    }

    > button {
        display: grid;
        flex: none;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        border-radius: 4px;
        place-items: center;
        background: none;
        color: var(--editor-muted, #55595f);
        cursor: pointer;

        &:hover {
            background: var(--editor-sunken, #e9ebed);
            color: var(--editor-fg, #262a30);
        }

        svg {
            width: 14px;
            height: 14px;
            fill: currentColor;
        }
    }
`;

// Una parola senza «*» vuol dire «che lo contiene»: il motore capisce solo «*», quindi la si scrive così.
const toPattern = (text: string) => (text.includes('*') ? text : `*${text}*`);

const PatternRow: FC<{
    value: string;
    autoFocus?: boolean;
    placeholder: string;
    count: (pattern: string) => string;
    removeLabel: string;
    onCommit: (pattern: string) => void;
    onRemove: () => void;
}> = ({ value, autoFocus, placeholder, count, removeLabel, onCommit, onRemove }) => {
    const [text, setText] = useState(value);
    const commit = () => {
        const trimmed = text.trim();
        if (!trimmed) return onRemove();
        const pattern = toPattern(trimmed);
        setText(pattern);
        if (pattern !== value) onCommit(pattern);
    };
    return (
        <PatternRowFrame>
            <SearchIcon aria-hidden='true' />
            <input
                value={text}
                autoFocus={autoFocus}
                placeholder={placeholder}
                aria-label={placeholder}
                onChange={(event) => setText(event.currentTarget.value)}
                onBlur={commit}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') (event.currentTarget as HTMLInputElement).blur();
                    if (event.key === 'Escape') {
                        if (!value) onRemove();
                        else setText(value);
                    }
                }}
            />
            {text.trim() && <span>{count(toPattern(text.trim()))}</span>}
            <ZTooltip title={removeLabel} position='top'>
                <button type='button' aria-label={removeLabel} onMouseDown={(event) => event.preventDefault()} onClick={onRemove}>
                    <CloseIcon />
                </button>
            </ZTooltip>
        </PatternRowFrame>
    );
};

// I MODELLI DI NOME: «*» per tutti, «Seat*» per quelli che cominciano così, «Seat» per quelli che lo contengono. Il motore
// legge ogni voce come testo, col solo «*» che vale «qualunque cosa», nell'ordine scritto: parole in qualunque ordine non
// si possono dire. Si aggiungono da «Match by name…» in fondo all'elenco (prototipo), non scrivendoli nella ricerca.
const PatternSlots: FC<{
    id: string;
    value: string[];
    options: ComposerSlotOption[];
    onChange: (ids: string[]) => void;
    isError?: boolean;
    allowPatterns?: boolean;
    texts: PatternTexts;
}> = ({ allowPatterns, options, value, onChange, texts, ...props }) => {
    const [drafting, setDrafting] = useState(false);
    const patternGlyph = <PatternIcon />;
    const count = (pattern: string) => {
        const expression = patternOf(pattern);
        return texts.countLabel(options.filter((option) => expression.test(option.label)).length);
    };
    const patterns = allowPatterns ? value.filter(isPattern) : [];
    const ids = value.filter((entry) => !patterns.includes(entry));
    const removeLabel = T._('Remove', 'Admin');
    const placeholder = T._('Words in the name, or * for any text', 'Admin');
    return (
        <ComposerSlotList
            {...props}
            value={ids}
            onChange={(next) => onChange([...next, ...patterns])}
            options={options}
            glyph={texts.glyph}
            caption={texts.caption}
            emptyLabel={texts.emptyLabel}
            noOptionsLabel={texts.noOptionsLabel}
            searchPlaceholder={texts.searchLabel}
            fromQuery={
                allowPatterns
                    ? (query) =>
                          // Senza testo, in testa c'è «tutti» (il modello «*»).
                          !query && !value.includes('*') ? { value: '*', label: texts.allLabel, caption: `* · ${count('*')}`, mark: patternGlyph } : null
                    : undefined
            }
            extraRows={
                allowPatterns && (
                    <>
                        {patterns.map((pattern) => (
                            <PatternRow
                                key={pattern}
                                value={pattern}
                                placeholder={placeholder}
                                count={count}
                                removeLabel={removeLabel}
                                onCommit={(next) =>
                                    onChange(
                                        value.map((entry) => (entry === pattern ? next : entry)).filter((entry, index, all) => all.indexOf(entry) === index)
                                    )
                                }
                                onRemove={() => onChange(value.filter((entry) => entry !== pattern))}
                            />
                        ))}
                        {drafting && (
                            <PatternRow
                                value=''
                                autoFocus
                                placeholder={placeholder}
                                count={count}
                                removeLabel={removeLabel}
                                onCommit={(next) => {
                                    setDrafting(false);
                                    if (!value.includes(next)) onChange([...value, next]);
                                }}
                                onRemove={() => setDrafting(false)}
                            />
                        )}
                    </>
                )
            }
            footer={
                allowPatterns
                    ? {
                          label: T._('Match by name…', 'Admin'),
                          caption: T._('By words in their name', 'Admin'),
                          mark: <SearchIcon />,
                          onClick: () => setDrafting(true)
                      }
                    : undefined
            }
        />
    );
};

type PatternSlotsProps = {
    id: string;
    value: string[];
    options: ComposerSlotOption[];
    onChange: (ids: string[]) => void;
    isError?: boolean;
    allowPatterns?: boolean;
};

/** Gli oggetti della scena. Con `allowPatterns`, anche «*» e i modelli di nome. */
export const ComposerObjectSlots: FC<PatternSlotsProps> = (props) => (
    <PatternSlots
        {...props}
        texts={{
            glyph: <ObjectsIcon />,
            caption: T._('Object', 'Admin'),
            emptyLabel: T._('Choose an object', 'Admin'),
            noOptionsLabel: T._('The scene has no objects yet.', 'Admin'),
            searchLabel: T._('Search objects…', 'Admin'),
            allLabel: T._('All objects', 'Admin'),
            countLabel: (count) => (count === 1 ? T._('1 object now', 'Admin') : stringFormat(T._('{0} objects now', 'Admin'), count.toString()))
        }}
    />
);

/** I materiali della scena, coi modelli di nome come gli oggetti (prototipo, Match by name; utente 2026-10-20). */
export const ComposerMaterialSlots: FC<PatternSlotsProps> = (props) => (
    <PatternSlots
        {...props}
        texts={{
            glyph: <MaterialsIcon />,
            caption: T._('Material', 'Admin'),
            emptyLabel: T._('Choose a material', 'Admin'),
            noOptionsLabel: T._('The scene has no materials yet.', 'Admin'),
            searchLabel: T._('Search materials...', 'Admin'),
            allLabel: T._('All materials', 'Admin'),
            countLabel: (count) => (count === 1 ? T._('1 material now', 'Admin') : stringFormat(T._('{0} materials now', 'Admin'), count.toString()))
        }}
    />
);
